import { useCallback } from 'react';

import { GroupingModeEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import type { SwitchGroupingModeValues } from '../SwitchGroupingMode';
import type { SwitchGroupingModeMutation } from '../types/props.type';
import type { SubmitHandler } from '../types/submit-handler.type';

import { useDirectMutationCallbacks } from './useDirectMutationCallbacks.hook';

interface Params {
  readonly mutation: SwitchGroupingModeMutation;
  readonly onSuccess: () => void;
  readonly queryKeysToInvalidate: readonly (readonly string[])[];
  readonly serviceUserId: Id;
}

export const useSwitchGroupingModeSubmit = ({
  mutation,
  onSuccess,
  queryKeysToInvalidate,
  serviceUserId,
}: Readonly<Params>) => {
  const buildCallbacks = useDirectMutationCallbacks({
    onSuccess,
    queryKeysToInvalidate,
  });

  const handleSubmitFactory = useCallback(
    (
      targetGroupingMode: GroupingModeEnum,
    ): SubmitHandler<SwitchGroupingModeValues> =>
      ({ values, memos, formikHelpers: { setSubmitting }, setFormError }) => {
        setFormError(null);

        const isSwitchingToManual =
          targetGroupingMode === GroupingModeEnum.Manual;

        mutation(
          {
            groupingMode: targetGroupingMode,
            label: isSwitchingToManual ? values.label.trim() : null,
            serviceUserId,
            versionData: memoForm2ApiMapper(memos),
          },
          buildCallbacks({
            setFormError,
            setSubmitting,
            successMessage: isSwitchingToManual
              ? 'Les prestations ont bien été regroupées.'
              : 'Les groupes ont bien été supprimés.',
          }),
        );

        return undefined;
      },
    [buildCallbacks, mutation, serviceUserId],
  );

  return { handleSubmitFactory };
};
